import { BrickWall, Zap, Ruler, FileCheck, Plus } from "lucide-react";
import ChapterHeading from "./ChapterHeading";
import Reveal from "./Reveal";
import { CallButton } from "./Buttons";

const CHECKS = [
  { icon: BrickWall, label: "Wall type check (brick or stud)" },
  { icon: Zap, label: "Pipework & power risk check" },
  { icon: Ruler, label: "Practical placement & height advice" },
  { icon: FileCheck, label: "No OT report needed" },
];

const FAQS = [
  {
    q: "Do I need an occupational therapist (OT) report?",
    a: "No. If you have one, we'll follow it exactly. If not, we can visit your home and assess the space with you before recommending anything.",
  },
  {
    q: "What if I don't know where the rails should go?",
    a: "That's exactly what the home assessment is for. We'll look at how you use the space and suggest practical placement, heights and the right rails or ramps for it.",
  },
  {
    q: "Can you install into my walls?",
    a: "In most cases, yes. We check whether walls are brick or stud and look for pipework and power risks before drilling. If a spot isn't suitable, we'll tell you honestly and suggest alternatives.",
  },
  {
    q: "Do you charge for a quote?",
    a: "Quotes are free — send us photos and a few details anytime. If you'd like us to visit and assess first, a call-out assessment fee applies, always confirmed before we come out.",
  },
  {
    q: "I'm renting / in a strata property — is that okay?",
    a: "Yes, though it's best to check with your landlord or strata first. We're happy to provide details of the proposed work to help with approval.",
  },
  {
    q: "Which areas do you service?",
    a: "The Perth metro area — from Joondalup to Mandurah and everywhere in between. Not sure if we cover you? Just ask.",
  },
];

export default function AssessmentFAQ() {
  return (
    <>
      <section data-testid="assessment-section" className="bg-charcoal text-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <ChapterHeading
                num="07"
                eyebrow="Home assessments"
                title="NOT SURE WHAT YOU NEED? WE'LL COME TO YOU."
                copy="Have an occupational therapist's report? We'll follow it exactly. No report? No problem — we can visit your home and assess the space with you: checking wall types, looking for pipework and power risks, and helping you work out practical placement based on how you actually use the space. A call-out assessment fee applies."
                dark
                testid="assessment-heading"
              />
              <Reveal delay={0.15}>
                <div className="mt-8">
                  <CallButton label="BOOK AN ASSESSMENT" variant="solid-light" testid="assessment-call-button" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <ul className="space-y-3">
                {CHECKS.map((check, i) => (
                  <Reveal key={check.label} delay={0.1 + i * 0.06}>
                    <li
                      data-testid={`assessment-check-${i}`}
                      className="flex min-h-[56px] items-center gap-4 rounded-2xl border border-white/15 px-5 py-4 transition-colors duration-300 hover:border-sage-tint/50"
                    >
                      <check.icon className="h-5 w-5 shrink-0 text-sage-tint" aria-hidden="true" />
                      <span className="text-sm font-semibold text-ivory/90">{check.label}</span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="faq-section" className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-28">
          <ChapterHeading
            num="08"
            eyebrow="Common questions"
            title="Good questions, straight answers."
            testid="faq-heading"
          />
          <div className="mt-12 grid gap-4 lg:grid-cols-2">
            {FAQS.map((faq, i) => (
              <Reveal key={faq.q} delay={Math.min(i * 0.05, 0.2)}>
                <details
                  data-testid={`faq-item-${i}`}
                  className="group h-full rounded-2xl border border-line bg-white p-6 transition-colors duration-300 open:border-sage"
                >
                  <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-4 font-display text-base font-bold text-charcoal sm:text-lg">
                    {faq.q}
                    <Plus className="h-5 w-5 shrink-0 text-sage-dark transition-transform duration-300 group-open:rotate-45" aria-hidden="true" />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-ink/75 sm:text-base">{faq.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
